import { pickAppCopy, type SupportedLocale } from "@patient-watch/i18n";
import { useAppLocale } from "./LocaleContext";
import { patientsCopyEn } from "@patient-watch/i18n/webapp/en-GB/patientsCopy";
import { patientsCopyDe } from "@patient-watch/i18n/webapp/de-DE/patientsCopy";
import { patientsCopyDa } from "@patient-watch/i18n/webapp/da-DK/patientsCopy";
import { patientsCopyEnUs } from "@patient-watch/i18n/webapp/en-US/patientsCopy";
import { patientsCopyFr } from "@patient-watch/i18n/webapp/fr-FR/patientsCopy";
import { patientsCopyIt } from "@patient-watch/i18n/webapp/it-IT/patientsCopy";
import { patientsCopyEs } from "@patient-watch/i18n/webapp/es-ES/patientsCopy";

interface PatientsCopy {
    // Navigation & breadcrumbs
    patients: string;
    navPatientsDescription: string;
    patientFallback: string;
    action: string;
    trackerEntry: string;
    patientsError: string;
    error: string;
    // List / table
    tabActive: string;
    tabPending: string;
    tabInactive: string;
    search: string;
    searchPatientsAria: string;
    searchForPatient: string;
    searchDescription: string;
    searchPlaceholder: string;
    noResultsFound: string;
    trySearchingHint: string;
    enterSearchTerm: string;
    loadingDiaries: string;
    errorLoadingDiaries: (message: string) => string;
    unknownPatient: string;
    dateOfBirthLabel: string;
    noDiariesOnPage: string;
    showingDiaries: (start: number, end: number, total: number) => string;
    patientsPerPage: string;
    emptyFirstPatientTitle: string;
    emptyFirstPatientDescription: string;
    emptyFirstPatientCta: string;
    emptyFirstPatientHelpLink: string;
    emptyTabTitle: (tabLabel: string) => string;
    emptyTabDescription: string;
    notDefined: string;
    // Column headers
    columnDiaryStatus: string;
    columnStartDate: string;
    columnPatientName: string;
    columnOrganisation: string;
    columnLabels: string;
    columnDateOfBirth: string;
    columnHospitalNumber: string;
    columnRead: string;
    // Filters / sort
    filters: string;
    filterDiariesAria: string;
    viewSettings: string;
    viewSettingsDescription: string;
    sharedWithClinicians: string;
    diaryStatus: string;
    selectStatuses: string;
    labels: string;
    organisation: string;
    save: string;
    apply: string;
    sortDiariesTitle: string;
    sortDiariesDescription: string;
    diaryScope: string;
    directAccess: string;
    allDiariesInOrganisation: string;
    selectClinicians: string;
    selectLabels: string;
    clearFilters: string;
    selectOrganisations: string;
    sortDiariesAria: string;
    sortBy: string;
    order: string;
    sortAscending: string;
    sortDescending: string;
    sortState: string;
    sortStartDate: string;
    sortFirstName: string;
    sortLastName: string;
    sortDateOfBirth: string;
    sortHospitalNumber: string;
    // Read / unread
    read: string;
    unread: string;
    // Diary panel
    noDiarySelected: string;
    refreshDiary: string;
    noDiaryFound: string;
    diaryCouldNotLoad: string;
    profileCouldNotLoad: string;
    diaryDetails: string;
    patientDetails: string;
    unknownState: string;
    edit: string;
    done: string;
    none: string;
    contact: string;
    noContactPerson: string;
    lastEntry: (relative: string) => string;
    diaryOrganisation: string;
    organisationAndContact: string;
    editCareDetails: string;
    labelsStudyTrial: string;
    contactName: string;
    contactLabel: (name: string) => string;
    nameLabel: string;
    emailLabel: string;
    openLink: string;
    dateRangeToNow: (start: string) => string;
    dateRangeTo: (start: string, end: string) => string;
    // Clinician tasks callout
    informationToComplete: string;
    clinicianTasks: (entryCount: number, formCount: number) => string;
    completeNextForm: string;
    completeForm: (title: string) => string;
    questionnaire: string;
    // Interface buttons
    more: string;
    moreActions: string;
    moreActionsAria: string;
    printDiary: string;
    shareDiary: string;
    openFullScreen: string;
    messageHistory: string;
    deleteDiary: string;
    deleteDiaryTitle: string;
    deleteDiaryDescription: string;
    cancel: string;
    delete: string;
    // Update / contact dropdowns
    updateDiary: string;
    addToDiary: string;
    addEvent: string;
    addConsent: string;
    addQuestionnaire: string;
    addFromTemplate: string;
    addEventDescription: string;
    addConsentDescription: string;
    addQuestionnaireDescription: string;
    addFromTemplateDescription: string;
    addConsentTitle: string;
    addConsentDescriptionLong: string;
    selectProcedureDate: string;
    notForAnEvent: string;
    consentDate: string;
    noProcedureDatesForConsent: string;
    addEventFirst: string;
    selectConsent: string;
    selectConsentHelp: string;
    noPublishedConsents: string;
    consentRoles: string;
    consentRolesHelp: string;
    saveConsent: string;
    savingConsent: string;
    existingDateGroup: string;
    newDate: string;
    newDateHelp: string;
    pickExistingDateHelp: string;
    markAsReviewed: string;
    endDiary: string;
    reinstate: string;
    contactPatient: string;
    // Dialogs
    endDiaryTitle: string;
    endDiaryNotifyQuestion: string;
    justEndDiary: string;
    endDiaryAndNotify: string;
    markReviewedTitle: string;
    markReviewedNotifyQuestion: string;
    justMarkReviewed: string;
    notifyPatient: string;
    // Email composer
    from: string;
    to: string;
    subject: string;
    emailBody: string;
    send: string;
    sending: string;
    emailSentSuccess: string;
    emailSentDescription: string;
    emailSendFailed: string;
    // Profile card
    seeYourPatients: string;
    seeYourPatientsDescription: string;
    goToPatients: string;
    goingToPatients: string;
    // Email delivery warning
    reliableEmailWarning: string;
    // Entry complete
    entryRecordedTitle: string;
    entryRecordedPending: (count: number) => string;
    entryRecordedComplete: string;
    continueToNextEntry: string;
    returnToPatient: string;
    // Update flow
    loadingTemplates: string;
    addToDiaryTitle: string;
    addToDiaryDescription: string;
    addManually: string;
    addManuallyDescription: string;
    chooseTemplate: string;
    chooseTemplateDescription: string;
    reviewTemplate: string;
    reviewTemplateDescription: string;
    confirmAndContinue: string;
    notifyPatientTitle: string;
    intervention: string;
    openingTemplateProcedure: string;
    existingQuestionnaires: string;
    continueWithAllQuestionnaires: string;
    upcomingEntries: string;
    noUpcomingEntries: string;
    moreEntriesSummary: (count: number) => string;
    unlocks: (date: string) => string;
    // Toasts
    diaryEnded: string;
    failedToEndDiary: string;
    tableSettingsSaved: string;
    diaryAlreadyRead: string;
    diaryReviewedSubject: string;
    // New diary dialog
    newDiary: string;
    searchTemplatesPlaceholder: string;
    noTemplatesYet: string;
    noTemplatesMatch: string;
    // Template picker
    searchTemplatesShort: string;
    procedureFilterInjection: string;
    procedureFilterSurgery: string;
    procedureFilterShockwave: string;
    procedureFilterConsultation: string;
    procedureFilterProduct: string;
    // Existing trackers step
    checkingActiveQuestionnaires: string;
    stopQuestionnairesDescription: string;
    noActiveQuestionnairesDescription: string;
    stopping: string;
    stopAllCurrentQuestionnaires: string;
    stopOnly: (name: string) => string;
    stopJoined: (list: string) => string;
    stopTrackerAria: (name: string) => string;
    checkingActiveBeforeTemplate: string;
    templateExistingTrackersDescription: string;
    // New diary dialog
    newPatientDiary: string;
    newPatientDiaryDescription: string;
    createTemplate: string;
    favourites: string;
    allTemplates: string;
    needFiltersHint: string;
    browseAllTemplates: string;
    // Email form
    cc: string;
    saving: string;
    // Intervention step
    interventionTypeQuestion: string;
    selectInterventionRequired: string;
    // Template card
    questionnairesLabel: string;
    continueLabel: string;
    notifyPatientUpdatedDescription: string;
    noJustFinish: string;
    yesNotifyPatient: string;
    diaryUpdatedFromTemplate: string;
    couldNotApplyTemplate: string;
    updateMessageTitle: string;
    updateMessageDescription: string;
    noJustSave: string;
    yesSendMessage: string;
    saveAndMessagePatient: string;
    saveOnly: string;
    updateQuestionnairesTitle: string;
    updateQuestionnairesDescription: string;
    selectQuestionnaire: string;
    selectQuestionnaireHelp: string;
    selectSchedule: string;
    selectScheduleHelp: string;
    addAnotherQuestionnaire: string;
    continueWithoutQuestionnaires: string;
    referenceDateTitle: string;
    referenceDateDescription: string;
    contactStepDescription: string;
    questionnaireWithIndex: (index: number) => string;
    setFlag: string;
    editStatus: string;
    setFlagTitle: string;
    setFlagDescription: string;
    flagOnTrack: string;
    flagImproved: string;
    flagWorsening: string;
    flagNotImproving: string;
    flagNoteLabel: string;
    flagNotePlaceholder: string;
    saveFlag: string;
    resumeAutomatic: string;
    resumeAutomaticHint: string;
    setManually: string;
    setManuallyBy: (name: string) => string;
    teamRecordTitle: string;
    teamRecordPrivate: string;
    notesTitle: string;
    notesDescription: string;
    noteByline: (author: string, when: string) => string;
    noteJustNow: string;
    addNote: string;
    notePlaceholder: string;
    saveNote: string;
    editNote: string;
    addNoteName: string;
    noteNameLabel: string;
    saveNoteEdit: string;
    deleteNote: string;
    deleteNoteTitle: string;
    deleteNoteConfirm: string;
    noteDeleted: string;
    couldNotDeleteNote: string;
    cancelNoteEdit: string;
    noteEdited: string;
    statusChangedTo: string;
    statusSetVerb: string;
    statusResumed: (name: string) => string;
    noTeamRecord: string;
    couldNotSaveFlag: string;
    couldNotSaveNote: string;
    flagSaved: string;
    noteSaved: string;
}

const patientsCopy: Record<SupportedLocale, PatientsCopy> = {
    "en-GB": patientsCopyEn,
    "en-US": patientsCopyEnUs,
    "de-DE": patientsCopyDe,
    "da-DK": patientsCopyDa,
    "fr-FR": patientsCopyFr,
    "it-IT": patientsCopyIt,
    "es-ES": patientsCopyEs,
};

export function usePatientsCopy(): PatientsCopy {
    const { locale } = useAppLocale();
    return pickAppCopy(patientsCopy, locale);
}

export function getPatientsCopy(locale: SupportedLocale): PatientsCopy {
    return pickAppCopy(patientsCopy, locale);
}
